/**
 * DialIQ — Type definitions
 *
 * Shared TypeScript types used by:
 *   - API client (redialService.ts)
 *   - Banner, Chips, Toast, Settings components
 *   - Leads page integration
 */

// ────────────────────────────────────────────────────────────
// Windows
// ────────────────────────────────────────────────────────────

export type RedialWindow = "am" | "pm" | "evening";

export interface WindowRange {
  startHour: number; // inclusive, 24h (e.g. 8 for 8am)
  endHour: number; // exclusive, 24h (e.g. 10 for 10am window end)
  label: string;
}

export const WINDOW_META: Record<
  RedialWindow,
  {
    label: string;
    short: string;
    accent: string; // used for gradient / active color
    accentMuted: string;
    timeLabel: string;
    opensLabel: string;
    iconName: "sunrise" | "sun" | "moon";
  }
> = {
  am: {
    label: "Morning",
    short: "AM",
    accent: "#F59E0B", // amber-500
    accentMuted: "#FEF3C7", // amber-100
    timeLabel: "8–10 AM",
    opensLabel: "8 AM",
    iconName: "sunrise",
  },
  pm: {
    label: "Afternoon",
    short: "PM",
    accent: "#EA580C", // orange-600
    accentMuted: "#FFEDD5", // orange-100
    timeLabel: "3–5 PM",
    opensLabel: "3 PM",
    iconName: "sun",
  },
  evening: {
    label: "Evening",
    short: "EVE",
    accent: "#6366F1", // indigo-500
    accentMuted: "#E0E7FF", // indigo-100
    timeLabel: "7–9 PM",
    opensLabel: "7 PM",
    iconName: "moon",
  },
};

// ────────────────────────────────────────────────────────────
// Lead previews
// ────────────────────────────────────────────────────────────

export interface RedialLeadPreview {
  id: string;
  name: string | null;
  phone: string | null;
  state: string | null;
  localTime?: string;
  lastNoAnswerAt?: string;
  noAnswerCountInWindow?: number;
}

// ────────────────────────────────────────────────────────────
// Daily digest
// ────────────────────────────────────────────────────────────

export interface DailyDigest {
  date: string; // YYYY-MM-DD
  userTimezone: string; // IANA tz
  alreadyApproved: boolean;
  am: RedialLeadPreview[];
  pm: RedialLeadPreview[];
  evening: RedialLeadPreview[];
  totalUnanswered: number;
}

export interface ApproveDigestPayload {
  date: string;
  leadIds: {
    am: string[];
    pm: string[];
    evening: string[];
  };
  snapshot?: unknown;
}

export interface ApproveDigestResult {
  amCount: number;
  pmCount: number;
  eveningCount: number;
}

// ────────────────────────────────────────────────────────────
// Chip counts (live)
// ────────────────────────────────────────────────────────────

export interface RedialWindowCount {
  active: number;
  total: number;
  /** ISO time the next queued lead's window opens. Absent on older backends. */
  nextOpenAt?: string | null;
}

export interface RedialChipCounts {
  am: RedialWindowCount;
  pm: RedialWindowCount;
  evening: RedialWindowCount;
}

// ────────────────────────────────────────────────────────────
// Lead list for dialer loading
// ────────────────────────────────────────────────────────────

export interface RedialListLead {
  id: string;
  name: string | null;
  phone: string | null;
  state: string | null;
  status: string | null;
  redial_window: RedialWindow;
  redial_cycle_count: number;
}

export interface RedialListResult {
  window: RedialWindow;
  count: number;
  leads: RedialListLead[];
}

// ────────────────────────────────────────────────────────────
// DialIQ hook result (from dialer saveCallResult)
// ────────────────────────────────────────────────────────────

export interface DialIQHookResult {
  advanced: boolean;
  terminal: boolean;
  fromWindow?: RedialWindow;
  toWindow?: RedialWindow | null;
  message?: string;
}

// ────────────────────────────────────────────────────────────
// Config
// ────────────────────────────────────────────────────────────

export interface DialIQConfig {
  dialWindows: Record<RedialWindow, WindowRange>;
  threshold: number;
  lookbackDays: number;
}

// ────────────────────────────────────────────────────────────
// User settings
// ────────────────────────────────────────────────────────────

export interface DialIQUserSettings {
  enabled: boolean;
  windows: Record<RedialWindow, { start: number; end: number }>;
  threshold: number;
  lookbackDays: number;
  excludedStatuses: string[];
}

export const DEFAULT_USER_SETTINGS: DialIQUserSettings = {
  enabled: true,
  windows: {
    am: { start: 8, end: 10 },
    pm: { start: 15, end: 17 },
    evening: { start: 19, end: 21 },
  },
  threshold: 5,
  lookbackDays: 30,
  // Mid-process or terminal statuses — filtered out of the redial queue by default.
  // Users can customize this list (add custom pipeline stages, or remove defaults).
  excludedStatuses: [
    "Sold & Approved",
    "Underwriting",
    "Appt",
    "Do not call",
    "Bad number",
    "Lapsed",
    "Declined",
  ],
};
